import { overlapsRange, type CalendarItem, type CalendarItemKind, type CalendarRange } from './calendar-item';
import {
  fromWireItem,
  goalItems,
  routineItems,
  taskItems,
  wixMediaId,
  type CalendarGoal,
  type CalendarRoutine,
  type CalendarTask,
  type WireCalendarItem,
} from './calendar-normalize';

// The Calendar's reads turned into what it draws: which answers count as failed, what is kept from the last
// read, the layers each source brings, and the one merged list. Pure, so the shared contract
// (`native-apps/shared/calendar/contract.json`) can run it as the phones run their ports of it. The hooks around
// it (`use-calendar-items.ts`) only fetch and hold state.

export type CalendarCompleteness = 'complete' | 'stale' | 'partial' | 'truncated' | 'failed';

export interface CalendarLayer {
  /** `<kind>` for the Symphony layers, `meeting:<account>:<sub-calendar>` / `booking:<site>` for the outside ones. */
  id: string;
  kind: CalendarItemKind;
  label?: string;
  /** Shown until the reader decides otherwise — a sub-calendar starts as the provider's own UI has it. */
  shownByDefault: boolean;
  /** `shownByDefault` is the provider's own answer, not this app's guess from who owns the calendar. */
  shownByProvider?: boolean;
  /** The provider's colour for it, as hex. */
  color?: string;
  /** Where it comes from, for the chip's mark. */
  source?: CalendarItem['source'];
  /** A meeting layer that is the account's own main calendar. */
  primary?: boolean;
}

/** A connected account's sub-calendar, as the harness lists it. */
interface WireCalendar {
  id: string;
  name: string;
  primary?: boolean;
  shownByDefault: boolean;
  shownByProvider: boolean;
  color?: string;
  access?: 'owner' | 'writer' | 'reader' | 'free_busy';
}

export interface WireAccount {
  id: string;
  email: string;
  provider: string;
  status: 'ok' | 'failed';
  reconnectRequired?: boolean;
  calendars: WireCalendar[];
  unreadCalendars: string[];
  partialCalendars: string[];
  /** The unread ones by id: two sub-calendars can share a name. */
  unreadCalendarIds?: string[];
}

export interface Meetings {
  accounts: WireAccount[];
  items: CalendarItem[];
  failed: string[];
  completeness?: CalendarCompleteness;
}

/** `GET /v1/calendar/meetings`, as the harness answers it. */
export interface MeetingsAnswer {
  accounts?: WireAccount[];
  items?: WireCalendarItem[];
  timeZone?: string;
  completeness?: { state?: CalendarCompleteness };
}

export interface BookingsSite {
  siteId: string;
  name?: string;
  logoMediaId?: string;
  hasBookings: boolean;
  status: 'ok' | 'partial' | 'truncated' | 'failed';
  failureReason?: 'permission_denied';
  staff: { id: string; resourceId: string; name: string }[];
  items: CalendarItem[];
}

export interface Bookings {
  sites: BookingsSite[];
  failed: string[];
  permissionDenied?: boolean;
  completeness?: CalendarCompleteness;
}

/** `GET /v1/calendar/bookings`, as the harness answers it. */
export interface BookingsAnswer {
  sites?: (Omit<BookingsSite, 'items' | 'logoMediaId' | 'staff'> & {
    logoMediaId?: string;
    logoUrl?: string;
    staff?: { id: string; resourceId: string; name: string }[];
    items?: WireCalendarItem[];
  })[];
  timeZone?: string;
  completeness?: { state?: CalendarCompleteness };
}

export const fromWire = (items: WireCalendarItem[] | undefined, timeZone?: string): CalendarItem[] =>
  (items ?? []).map((item) => fromWireItem(item, timeZone)).filter((item): item is CalendarItem => !!item);

/**
 * Every connected account's meetings. What could not be read keeps what it showed before (`previous`),
 * so a provider's passing hiccup leaves the calendar as it was instead of emptying part of it: a whole
 * account that fails keeps its events and its sub-calendars (and with them which are hidden), a
 * sub-calendar the harness names as unread keeps its events. Either counts as failed, and so does a
 * sub-calendar that only came back in part. An account that must be reconnected keeps nothing.
 */
export function meetingsFromAnswer(answer: MeetingsAnswer, previousAnswer?: Meetings): Meetings {
  const previous = previousAnswer?.items ?? [];
  const accounts = (answer.accounts ?? []).map((account) => {
    const before = previousAnswer?.accounts.find((known) => known.id === account.id);
    return account.status === 'failed' && !account.reconnectRequired && before ? { ...account, calendars: before.calendars } : account;
  });
  const items = fromWire(answer.items, answer.timeZone);
  const failed: string[] = [];
  let restored = false;
  for (const account of accounts) {
    if (account.status === 'failed') {
      failed.push(account.email);
      if (!account.reconnectRequired) {
        const retained = previous.filter((item) => item.sourceId === account.id);
        if (retained.length > 0) restored = true;
        items.push(...retained);
      }
      continue;
    }
    const unread = new Set(account.unreadCalendarIds ?? []);
    if (unread.size && !account.reconnectRequired) {
      const retained = previous.filter((item) => item.sourceId === account.id && item.calendar && unread.has(item.calendar.id));
      if (retained.length > 0) restored = true;
      items.push(...retained);
    }
    failed.push(...[...account.unreadCalendars, ...account.partialCalendars].map((name) => `${account.email} · ${name}`));
  }
  return {
    accounts,
    items,
    failed,
    completeness: restored ? 'stale' : answer.completeness?.state ?? (failed.length > 0 ? 'partial' : 'complete'),
  };
}

/**
 * Every site's Bookings sessions. A site the harness could not read in full is named as failed, by id; one
 * that had a complete answer before (`previousAnswer`) keeps that answer's rows and layer metadata.
 */
export function bookingsFromAnswer(answer: BookingsAnswer, previousAnswer?: Bookings): Bookings {
  let restored = false;
  const sites = (answer.sites ?? []).map(({ logoMediaId, logoUrl, staff, ...site }) => {
    const mediaId = wixMediaId(logoMediaId ?? logoUrl);
    const current: BookingsSite = {
      ...site,
      ...(mediaId ? { logoMediaId: mediaId } : {}),
      staff: staff ?? [],
      items: fromWire(site.items, answer.timeZone),
    };
    const complete = previousAnswer?.sites.find((known) => known.siteId === site.siteId && known.status === 'ok');
    if (current.status === 'ok' || !complete) return current;
    restored = true;
    return {
      ...current,
      name: current.name ?? complete.name,
      logoMediaId: current.logoMediaId ?? complete.logoMediaId,
      hasBookings: current.hasBookings || complete.hasBookings,
      staff: current.staff.length > 0 ? current.staff : complete.staff,
      items: complete.items,
    };
  });
  const failed = sites.filter((site) => site.status === 'failed' || (site.hasBookings && site.status !== 'ok')).map((site) => site.siteId);
  return {
    sites,
    failed,
    permissionDenied: sites.some((site) => site.failureReason === 'permission_denied'),
    completeness: restored
      ? 'stale'
      : answer.completeness?.state ?? (sites.some((site) => site.status === 'truncated') ? 'truncated' : failed.length ? 'partial' : 'complete'),
  };
}

/** The names of the sites `failed` lists, for the notice; a site with no name is left out. */
export function failedSiteNames(bookings: Bookings): string[] {
  const failed = new Set(bookings.failed);
  return bookings.sites.filter((site) => failed.has(site.siteId)).flatMap((site) => (site.name ? [site.name] : []));
}

/** The layer an item belongs to, matching `CalendarLayer.id`: a meeting's sub-calendar, a booking's site. */
export function layerOf(item: CalendarItem): string {
  if (item.kind === 'meeting') {
    return `meeting:${(item.sourceId ?? '').toLowerCase()}:${(item.calendar?.id ?? item.sourceLabel ?? '').toLowerCase()}`;
  }
  if (item.kind === 'booking') return `booking:${item.sourceId}`;
  return item.kind;
}

/**
 * One layer per sub-calendar, the way the provider lists them, whether or not the period shows it. An
 * account that could not be read keeps a layer of its own, so it can still be found and switched.
 */
export function meetingLayers(accounts: WireAccount[]): CalendarLayer[] {
  const layers = new Map<string, CalendarLayer>();
  for (const account of accounts) {
    for (const calendar of account.calendars) {
      const id = `meeting:${account.id.toLowerCase()}:${calendar.id.toLowerCase()}`;
      if (layers.has(id)) continue;
      layers.set(id, {
        id,
        kind: 'meeting',
        label: calendar.name,
        shownByDefault: calendar.shownByDefault,
        shownByProvider: calendar.shownByProvider,
        ...(calendar.color ? { color: calendar.color } : {}),
        source: { provider: account.provider },
        ...(calendar.primary ? { primary: true } : {}),
      });
    }
    const own = `meeting:${account.id.toLowerCase()}:${account.email.toLowerCase()}`;
    if (account.calendars.length === 0 && !layers.has(own)) {
      layers.set(own, { id: own, kind: 'meeting', label: account.email, shownByDefault: true, source: { provider: account.provider } });
    }
  }
  return [...layers.values()];
}

/** The sites that take bookings: a site with none is no layer and brings no items. */
export function bookingSitesOf(sites: BookingsSite[] | undefined): BookingsSite[] {
  return sites?.filter((site) => site.hasBookings) ?? [];
}

export function bookingLayers(sites: BookingsSite[]): CalendarLayer[] {
  return bookingSitesOf(sites).map((site) => ({
    id: `booking:${site.siteId}`,
    kind: 'booking' as const,
    label: site.name,
    shownByDefault: true,
    source: site.logoMediaId ? { logoMediaId: site.logoMediaId } : {},
  }));
}

/** The Bookings read settled and found no site; a failed or refused read keeps the tab so its notice shows. */
export function withoutBookings(
  layers: CalendarLayer[],
  sources: { kind: CalendarItemKind; loading: boolean; completeness: string; permissionDenied?: boolean }[],
): boolean {
  if (layers.some((layer) => layer.kind === 'booking')) return false;
  const source = sources.find((candidate) => candidate.kind === 'booking');
  return !!source && !source.loading && source.completeness !== 'failed' && !source.permissionDenied;
}

/**
 * Every layer, in the Filters order. Until a source answers, its chips are the ones it had last time
 * (`remembered`), so the row doesn't build up chip by chip.
 */
export function calendarLayers({
  meetings,
  bookings,
  remembered,
}: {
  /** The source's fresh layers, or `undefined` while it is still on its first answer. */
  meetings: CalendarLayer[] | undefined;
  bookings: CalendarLayer[] | undefined;
  remembered: CalendarLayer[];
}): CalendarLayer[] {
  const rememberedOf = (kind: CalendarItemKind) => remembered.filter((layer) => layer.kind === kind);
  return [
    ...(meetings ?? rememberedOf('meeting')),
    ...(bookings ?? rememberedOf('booking')),
    { id: 'task', kind: 'task', shownByDefault: true },
    { id: 'routine', kind: 'routine', shownByDefault: true },
    { id: 'goal', kind: 'goal', shownByDefault: true },
  ];
}

/**
 * A Space's own rows, plus the account's rows that belong to no Space: work with no Space is the owner's
 * all the same, so a Space's calendar shows it too. Unscoped, the rows are the account's own.
 */
export function withSpacelessRows<T extends { id: string; workspaceId?: string | null }>(
  workspaceId: string | undefined,
  own: T[],
  account: T[],
): T[] {
  if (!workspaceId) return own;
  const ids = new Set(own.map((row) => row.id));
  return [...own, ...account.filter((row) => !row.workspaceId && !ids.has(row.id))];
}

/**
 * Everything on the calendar inside `range`, in time order. Meetings and bookings belong to the account and
 * show in every Space; tasks, routines and goals are the Space's own plus the Space-less ones. A task carries
 * its agent's name and its goal's title when they are known.
 */
export function calendarItemsInRange({
  range,
  workspaceId,
  meetings,
  bookingSites,
  tasks,
  accountTasks = [],
  routines,
  accountRoutines = [],
  goals,
  agentNameOf = () => undefined,
  goalTitleOf = () => undefined,
}: {
  range: CalendarRange;
  workspaceId: string | undefined;
  meetings: CalendarItem[];
  /** Every site the bookings read answered; only those with bookings bring items. */
  bookingSites: BookingsSite[];
  /** `undefined` while the read has no answer. */
  tasks: CalendarTask[] | undefined;
  accountTasks?: CalendarTask[];
  routines: CalendarRoutine[] | undefined;
  accountRoutines?: CalendarRoutine[];
  goals?: CalendarGoal[];
  agentNameOf?: (agentId: string) => string | undefined;
  goalTitleOf?: (goalId: string) => string | undefined;
}): CalendarItem[] {
  const bookingList = bookingSitesOf(bookingSites).flatMap((site) => site.items);
  const taskRows = tasks ? withSpacelessRows(workspaceId, tasks, accountTasks) : [];
  const tasksById = new Map(taskRows.map((task) => [task.id, task]));
  const taskList = taskItems(taskRows).map((item) => {
    const task = item.ref.taskId ? tasksById.get(item.ref.taskId) : undefined;
    const agentName = task?.agentId ? agentNameOf(task.agentId) : undefined;
    const goalTitle = task?.goalId ? goalTitleOf(task.goalId) : undefined;
    return {
      ...item,
      ...(agentName ? { agentName } : {}),
      ...(goalTitle ? { goalTitle } : {}),
    };
  });
  const routineList = routines ? routineItems(withSpacelessRows(workspaceId, routines, accountRoutines)) : [];
  const goalList = goals
    ? goalItems(
        goals.filter((goal) => !workspaceId || !goal.workspaceId || goal.workspaceId === workspaceId),
        taskRows,
      )
    : [];
  return [...meetings, ...bookingList, ...taskList, ...routineList, ...goalList]
    .filter((item) => overlapsRange(item, range))
    .sort((a, b) => a.start - b.start || a.end - b.end);
}
